import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { SchedulingContextProvider } from './schedulingContext';
import { MessagesContextProvider } from './messagesContext/MessagesContext';
import { useMessengerService } from '../../services';

export type AppContextType = {
  appointmentScheduled: () => void;
  cancelScheduling: () => void;
};

export const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppContextProviderProps {
  children: ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = (
  props: AppContextProviderProps
) => {
  const messengerService = useMessengerService();

  const onBeforeUnload = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any): void => {
      event.preventDefault();
    },
    []
  );

  function removeBeforeUnloadEventListener(): void {
    window.removeEventListener('beforeunload', onBeforeUnload, false);
  }

  function appointmentScheduled(): void {
    removeBeforeUnloadEventListener();
    messengerService.appointmentScheduled();
  }

  function cancelScheduling(): void {
    removeBeforeUnloadEventListener();
    messengerService.cancelScheduling();
    return;
  }

  /* Currently, we are listening for the beforeunload event and popping up the
     generic browser leave site message by calling the preventDefault method of
     the event. This isn't the greatest way to handle things, but should suffice
     for now.
     
     At some point in the future, we should look at saving state when this event
     fires and then possibly reloading that state when the page is revisited at 
     some point later. There is also some documentation about using the visibilitychange
     event as a more reliable option due to issues with mobile.  More information
     can be found here: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event */
  useEffect(() => {
    window.addEventListener('beforeunload', onBeforeUnload, false);

    return (): void => {
      window.removeEventListener('beforeunload', onBeforeUnload, false);
    };
  }, [onBeforeUnload]);

  return (
    <AppContext.Provider
      value={{
        appointmentScheduled: appointmentScheduled,
        cancelScheduling: cancelScheduling,
      }}
    >
      <MessagesContextProvider>
        <SchedulingContextProvider>{props.children}</SchedulingContextProvider>
      </MessagesContextProvider>
    </AppContext.Provider>
  );
};
